<template>
  <div class="mission-list-pane" @scroll="handlePaneScroll">
    <div class="mission-list-pane__container">
      <div class="mission-list-pane__header" :class="{ 'is-fixed': isFixed }" ref="header">
        <mission-list-header-section
          :selectedMissionYear="selectedMissionYear"
          :isFixed="isFixed"
          :successfulCheckIn="successfulCheckIn"
          :checkInMissionsBeingHeldForThisSeason="checkInMissionsBeingHeldForThisSeason"
          @onChangeMissionYear="fetchMissionData"
          @onClickCheckIn="handleCheckInClicked"
        />
      </div>
      <div
        class="mission-list-pane__list"
        :class="{ 'is-fixed': state.doneFetch }"
        :style="state.listStyle"
      >
        <!-- ミッションごとの達成状況カード -->
        <mission-card-section
          v-for="(mission, key) in missionProgressCardData"
          :key="key"
          class="mission-list-pane__item"
          :mission="mission"
          :noSpace="mission.honeycombType === 'check'"
        >
          <!-- ヘッダーアイコン -->
          <template v-slot:header-icon>
            <mission-race-icon
              v-if="mission.headerIcon === 'race-icon'"
              class="mission-card-section__title-icon"
            />
            <mission-member-icon
              v-if="mission.headerIcon === 'member-icon'"
              class="mission-card-section__title-icon"
            />
            <mission-clip-icon
              v-else-if="mission.headerIcon === 'clip-icon'"
              class="mission-card-section__title-icon"
            />
            <mission-radiophone-icon
              v-else-if="mission.headerIcon === 'radiophone-icon'"
              class="mission-card-section__title-icon"
            />
            <mission-share-icon
              v-else-if="mission.headerIcon === 'share-icon'"
              :width="18"
              :height="18"
              class="mission-card-section__title-icon"
            />
            <mission-map-icon
              v-else-if="mission.headerIcon === 'map-icon'"
              class="mission-card-section__title-icon"
            />
          </template>
          <!-- ミッション達成状況カード -->
          <template v-slot:default>
            <!-- ミッション開催期間 -->
            <div
              v-if="!mission.hasCompleted && mission.missionTerm && mission.missionTermTitle"
              class="mission-card-section__mission-term"
            >
              <mission-term-parts :mission="mission" />
            </div>
            <!-- 達成カウント -->
            <div
              v-if="!mission.hasCompleted && mission.achievementCountDispType"
              class="mission-card-section__process"
            >
              <mission-process-parts :mission="mission" />
            </div>

            <!-- ハニカム -->
            <template v-if="mission.achievementCondition && mission.honeycombType">
              <!-- サーキット用のハニカム -->
              <mission-honey-comb-circuit-parts
                v-if="mission.honeycombType === 'circuit'"
                :mission="mission"
              />

              <!-- ハニカム 7または10個 -->
              <mission-honeycomb-seven-ten-parts
                v-else-if="
                  mission.achievementCondition.length === 7 ||
                  mission.achievementCondition.length === 10
                "
                class="mission-card-section__honeycomb"
                :class="{ 'is-completed': mission.hasCompleted }"
                :mission="mission"
                :checkInMissionsBeingHeld="checkInMissionsBeingHeld"
              />

              <!-- ハニカム 8個 -->
              <mission-honeycomb-eight-parts
                v-else-if="mission.achievementCondition.length === 8"
                :mission="mission"
                :checkInMissionsBeingHeld="checkInMissionsBeingHeld"
              />

              <!-- ハニカム 9個 -->
              <mission-honeycomb-nine-parts
                v-else-if="mission.achievementCondition.length === 9"
                :mission="mission"
                :checkInMissionsBeingHeld="checkInMissionsBeingHeld"
              />

              <!-- ハニカム 11個 -->
              <mission-honeycomb-eleven-parts
                v-else-if="mission.achievementCondition.length === 11"
                :mission="mission"
                :checkInMissionsBeingHeld="checkInMissionsBeingHeld"
              />

              <!-- ハニカム 12個 -->
              <mission-honeycomb-twelve-parts
                v-else-if="mission.achievementCondition.length === 12"
                :mission="mission"
                :checkInMissionsBeingHeld="checkInMissionsBeingHeld"
              />
            </template>
            <!-- ミッションチェックリスト -->
            <mission-check-list-parts v-if="mission.honeycombType === 'check'" :mission="mission" />
            <!-- 獲得済みポイント -->
            <mission-get-point-parts
              v-if="mission.earnedPoints"
              class="mission-card-section__get-point"
              :class="{ 'is-completed': mission.hasCompleted }"
              :mission="mission"
            />
            <!-- 紹介コード表示ボタン -->
            <mission-referral-code-parts
              v-if="mission.missionCode === 'INVITED_USING_REFERRAL_CODE' && !mission.hasCompleted"
            />
          </template>
          <!-- 達成状況 -->
          <template v-if="mission.achievementMethod" v-slot:howto>
            <p class="mission-card-section__how-title">
              {{ mission.achievementMethod.title }}
            </p>
            <p
              class="mission-card-section__how-description"
              v-html="mission.achievementMethod.text.replace(/\n/g, '<br/>')"
            ></p>
            <div
              v-if="mission.missionGroupCode === 'CHECKED_IN_BONUS'"
              class="mission-card-section__check-point"
            >
              <h3 class="mission-card-section__check-point-title">
                {{ $tc('MissionPage.checkPointList.title') }}
              </h3>
              <div v-for="[area, checkinPoint] in Object.entries(checkPointListByArea)" :key="area">
                <p class="mission-card-section__check-point-area">
                  {{
                    $t('MissionPage.checkPointList.area', {
                      area,
                    })
                  }}
                </p>
                <ul class="mission-card-section__check-point-list">
                  <li
                    v-for="checkPoint in checkinPoint"
                    :key="checkPoint.stadiumId"
                    class="mission-card-section__check-point-item"
                  >
                    <mission-check-in-point-section :checkPoint="checkPoint" />
                  </li>
                </ul>
              </div>
            </div>
            <p class="mission-card-section__how-info" v-if="mission.achievementMethod.infoLink">
              <a
                :href="mission.achievementMethod.infoLink"
                class="mission-card-section__how-info-link"
                target="_blank"
              >
                <span>{{ $tc('MissionPage.howToDetail') }}</span>
                <mission-external-link-icon />
              </a>
            </p>
            <p
              v-if="mission.achievementMethod.note"
              class="mission-card-section__how-note"
              v-html="mission.achievementMethod.note"
            />
          </template>
        </mission-card-section>
      </div>
    </div>
    <div class="mission-list-pane__background">
      <img :src="bgImagePath" class="mission-list-pane__background-image" alt="" />
    </div>

    <check-in-failed-modal-section
      v-if="isShowCheckInFailedDetailModal"
      @close="hideCheckInFailedDetailModal"
    />
    <mission-point-expiration-modal-section
      v-if="isShowPointExpirationModal"
      @close="hidePointExpirationModal"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  Ref,
} from '@vue/composition-api'
import dayjs from 'dayjs'
import { PluginApi } from 'vue-loading-overlay'
import { orderBy } from 'lodash'
import MissionListHeaderSection from '@/components/MissionPage/MissionListPane/MissionListHeaderSection.vue'
import MissionCardSection from '@/components/MissionPage/MissionListPane/MissionCardSection.vue'
import MissionProcessParts from '@/components/MissionPage/MissionListPane/parts/MissionProcessParts.vue'
import MissionGetPointParts from '@/components/MissionPage/MissionListPane/parts/MissionGetPointParts.vue'
import MissionRaceIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionRaceIcon.vue'
import MissionMemberIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionMemberIcon.vue'
import MissionClipIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionClipIcon.vue'
import MissionRadiophoneIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionRadiophoneIcon.vue'
import MissionHoneyCombCircuitParts from '@/components/MissionPage/MissionListPane/parts/MissionHoneyCombCircuitParts.vue'
import MissionHoneycombSevenTenParts from '@/components/MissionPage/MissionListPane/parts/MissionHoneycombSevenTenParts.vue'
import MissionHoneycombEightParts from '@/components/MissionPage/MissionListPane/parts/MissionHoneycombEightParts.vue'
import MissionHoneycombNineParts from '@/components/MissionPage/MissionListPane/parts/MissionHoneycombNineParts.vue'
import MissionHoneycombElevenParts from '@/components/MissionPage/MissionListPane/parts/MissionHoneycombElevenParts.vue'
import MissionHoneycombTwelveParts from '@/components/MissionPage/MissionListPane/parts/MissionHoneycombTwelveParts.vue'
import MissionShareIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionShareIcon.vue'
import MissionReferralCodeParts from '@/components/MissionPage/MissionListPane/parts/MissionReferralCodeParts.vue'
import MissionCheckListParts from '@/components/MissionPage/MissionListPane/parts/MissionCheckListParts.vue'
import MissionExternalLinkIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionExternalLinkIcon.vue'
import StoreUtil from '@/store/StoreUtil'
import useMissionProgressData, {
  MissionProgressCardType,
  CheckPointType,
} from '@/components/MissionPage/hook/useMissionProgressData'
import MissionProgressDocument from '@/store/stores/collectionModule/documents/mission/MissionProgressDocument'
import MissionMapIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionMapIcon.vue'
import I18n from '@/locales/I18n'
import CheckInFailedModalSection from '@/components/MissionPage/MissionListPane/CheckInFailedModalSection.vue'
import MissionTermParts from '@/components/MissionPage/MissionListPane/parts/MissionTermParts.vue'
import MissionCheckInPointSection from '@/components/MissionPage/MissionListPane/MissionCheckInPointSection.vue'
import MissionPointExpirationModalSection from '@/components/MissionPage/MissionListPane/MissionPointExpirationModalSection.vue'
import IndexedDBAccessor from '@/store/stores/IndexedDBstore/IndexedDBAccessor'
import useMissionTerm from '@/store/hook/mission/useMissionTerm'

/* eslint-disable @typescript-eslint/no-var-requires */
const bgImagePath = require('@/assets/img/MissionPage/bg.png')

/**
 * ミッション画面 チェックイン、ミッション達成状況一覧ペイン
 */
export default defineComponent({
  name: 'MissionListPane',
  components: {
    CheckInFailedModalSection,
    MissionTermParts,
    MissionCheckInPointSection,
    MissionMapIcon,
    MissionExternalLinkIcon,
    MissionCheckListParts,
    MissionReferralCodeParts,
    MissionRaceIcon,
    MissionShareIcon,
    MissionHoneyCombCircuitParts,
    MissionHoneycombSevenTenParts,
    MissionHoneycombEightParts,
    MissionHoneycombNineParts,
    MissionHoneycombElevenParts,
    MissionHoneycombTwelveParts,
    MissionRadiophoneIcon,
    MissionClipIcon,
    MissionMemberIcon,
    MissionGetPointParts,
    MissionProcessParts,
    MissionCardSection,
    MissionListHeaderSection,
    MissionPointExpirationModalSection,
  },
  setup() {
    const missionPageStore = StoreUtil.useStore('MissionPageStore')
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const {
      fetchMissionPageData,
      selectedMissionYear,
      checkIn,
      checkInMissionsBeingHeld,
      checkInMissionsBeingHeldForThisSeason,
      isShowCheckInFailedDetailModal,
      currentLocationInfo,
    } = missionPageStore
    const { fetchMypagePageData, fetchPreviousSeasonExpirationUserPoints } = mypagePageStore
    const { getMissionProgressCardData } = useMissionProgressData()
    const { getTargetYearMissionTerm, getThisSeasonYear } = useMissionTerm()
    const {
      getMissionPointExpirationLastDisplayedDate,
      setMissionPointExpirationLastDisplayedDate,
    } = IndexedDBAccessor()

    const loading = inject('loading') as PluginApi
    const header = ref<HTMLElement | null>(null)

    const state = reactive({
      scrollY: 0,
      fixPoint: 227,
      doneFetch: false,
      listStyle: {},
    })
    const isFixed = computed(() => state.scrollY > state.fixPoint)
    const manageListStyle = () => {
      if (header.value === null) {
        return
      }
      const headerHeight = header.value.getBoundingClientRect().bottom
      if (headerHeight === 0) {
        return
      }
      state.listStyle = {
        'padding-top': `${headerHeight - 48 - 10}px`,
      }
    }

    /** Mission達成状況カードデータ */
    const missionProgressCardData = ref([]) as Ref<Array<MissionProgressCardType>>
    /** エリアごとに分類されたイベントチェックインの場所 */
    const checkPointListByArea = ref({}) as Ref<Record<string, Array<CheckPointType>>>
    const successfulCheckIn = ref(false) as Ref<boolean>
    // ポイント有効期限のお知らせモーダルを表示するかどうか
    const isShowPointExpirationModal = ref(false)

    const handlePaneScroll = (event: Event) => {
      const paneElm = event.target as HTMLElement
      state.scrollY = paneElm?.scrollTop
    }

    /**
     * 対象年度のミッション達成状況を取得する
     */
    const fetchMissionData = async (changedYear: number) => {
      selectedMissionYear.value = changedYear
      await fetchMissionPageData(selectedMissionYear.value)
      missionProgressCardData.value = orderBy(
        Object.values(getMissionProgressCardData(changedYear)),
        'order',
      )

      // イベントチェックイン一覧情報をクリアする
      checkPointListByArea.value = {}
      const _missionProgressCardData = getMissionProgressCardData(changedYear)

      if (_missionProgressCardData.CHECKED_IN_BONUS) {
        orderBy(_missionProgressCardData.CHECKED_IN_BONUS.checkPointList, 'startDate')?.forEach(
          (checkinPoint) => {
            const area = checkinPoint.area?.[I18n.locale]
            if (!area) {
              return
            }
            if (checkPointListByArea.value[area]) {
              checkPointListByArea.value[area].push(checkinPoint)
            } else {
              checkPointListByArea.value[area] = [checkinPoint]
            }
          },
        )
      }
    }

    /**
     * チェックインの処理
     */
    const handleCheckInClicked = async () => {
      const loader = loading.show()
      const checkInResult = await checkIn()
      if (checkInResult) {
        successfulCheckIn.value = true
        await Promise.all([
          fetchMissionData(selectedMissionYear.value),
          fetchMypagePageData('', true),
        ])
      }
      loader.hide()
    }

    /**
     * チェックイン失敗詳細モーダル非表示
     */
    const hideCheckInFailedDetailModal = () => {
      isShowCheckInFailedDetailModal.value = false
      currentLocationInfo.value = null
    }

    /**
     * ポイント有効期限モーダル非表示
     */
    const hidePointExpirationModal = () => {
      const now = dayjs().valueOf()
      setMissionPointExpirationLastDisplayedDate({
        displayedDate: now,
      })

      isShowPointExpirationModal.value = false
    }

    /**
     * ポイント有効期限モーダルを表示するか確認
     */
    const checkToShowPointExpirationModal = async () => {
      const thisSeasonYear = getThisSeasonYear()
      if (thisSeasonYear <= 2024) {
        // 2024シーズン以前はモーダルを表示しない
        return
      }

      const thisSeasonMissionStartDate = getTargetYearMissionTerm(thisSeasonYear).startDate ?? 0

      const lastDisplayedDate = await getMissionPointExpirationLastDisplayedDate()

      if (thisSeasonMissionStartDate > lastDisplayedDate) {
        // 先シーズンに失効したポイント一覧を取得する
        await fetchPreviousSeasonExpirationUserPoints()
        // 新シーズンが始まってから最初にミッション一覧を開いた場合、モーダルを表示する
        isShowPointExpirationModal.value = true
      }
    }

    onMounted(async () => {
      const headerElm = document.querySelector('.header-pane') as HTMLElement
      const pointResultElm = document.querySelector('.mission-list-header__result') as HTMLElement
      state.fixPoint =
        pointResultElm.getBoundingClientRect().top - headerElm.getBoundingClientRect().height

      /**
       * ミッション一覧画面に必要なデータを取得する
       * - 対象年度のミッション達成状況
       * - 保有ポイント（マイページストアの情報を最新にする）
       */
      await Promise.all([
        fetchMissionData(selectedMissionYear.value),
        fetchMypagePageData('', true),
      ])
      manageListStyle()
      await checkToShowPointExpirationModal()

      state.doneFetch = true

      window.addEventListener('resize', manageListStyle)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', manageListStyle)
    })

    return {
      bgImagePath,
      state,
      isFixed,
      header,
      handlePaneScroll,
      fetchMissionData,
      selectedMissionYear: selectedMissionYear as Ref<number>,
      missionProgressCardData,
      checkPointListByArea,
      handleCheckInClicked,
      hideCheckInFailedDetailModal,
      hidePointExpirationModal,
      checkInMissionsBeingHeld: checkInMissionsBeingHeld as Ref<Array<MissionProgressDocument>>,
      checkInMissionsBeingHeldForThisSeason: checkInMissionsBeingHeldForThisSeason as Ref<
        Array<MissionProgressDocument>
      >,
      successfulCheckIn,
      isShowCheckInFailedDetailModal,
      isShowPointExpirationModal,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-list-pane {
  position: relative;
  height: 100%;
  overflow-y: auto;
  color: $white;

  &__header {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    padding: 16px;

    &.is-fixed {
      position: fixed;
      top: 48px;
      padding: 0;
    }
  }

  &__container {
    position: relative;
    z-index: 2;
    max-width: 632px;
    margin-right: auto;
    margin-left: auto;
  }

  &__list {
    display: flex;
    gap: 16px;
    flex-direction: column;
    min-height: 100%;
    padding: 339px 16px 16px;
    opacity: 0;

    &.is-fixed {
      opacity: 1;
    }
  }

  &__background {
    position: fixed;
    top: 48px;
    left: 0;
    z-index: 1;
    width: 100%;

    &::after {
      position: fixed;
      top: 64px;
      left: 0;
      width: 100%;
      content: '';
      background: linear-gradient(180deg, rgba(43, 43, 43, 0) 0%, #2b2b2b 100%);
      aspect-ratio: 375 / 184;
    }
  }

  &__background-image {
    width: 100%;
  }

  @media screen and (min-width: $responsiveTabletWidth) {
    &__background {
      &::after {
        height: 167px;
        aspect-ratio: auto;
      }
    }

    &__background-image {
      height: 183px;
      object-fit: cover;
    }
  }
}
</style>
